import * as React from 'react';
import styled from 'styled-components';
import { IconLandingBlogSearch } from '@doltech/icons/LandingBlog/IconLandingBlogSearch';
import { PaginationComponent } from '@doltech/ui/lib/figma/Pagination/Pagination';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { ImageEmptyResult } from '@doltech/images/EmptyResult';
import { Link } from '@doltech/ui/lib/figma/Link';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { useRouter } from 'next/router';

const Main = styled.div`
  display: grid;
  gap: 16px;
  .search-header {
    display: grid;
    grid-template-columns: 1fr max-content;
    align-items: center;
    background: ${colorsV2.gray10};
    border-radius: 8px;
    input {
      height: 40px;
      padding: 10px 12px;
      border: none;
      background-color: transparent;
      outline: none;
      ::placeholder {
        color: ${colorsV2.gray80};
      }
    }
    .icon-wrapper {
      font-size: 24px;
      line-height: 0;
      margin-right: 12px;
    }
  }
  .list {
    display: grid;
    grid-auto-rows: max-content;

    gap: 24px;
    ${fromScreen(776)} {
      gap: 32px;
    }
  }
  .empty-state {
    text-align: center;
  }
`;

interface ListingForLandingBlogProps {
  data: any[];
  renderCard: ({ item, index }) => JSX.Element;
  renderSeparator?: () => JSX.Element;

  onPageChange?: (page: number, pageSize: number) => void;
  pageSize?: number;
  renderKey?: string;
  pageInfo: any;

  onSearch?: (text: string) => void;
  searchPlaceholder?: string;
  searchResults?: any;
  currentPageUrl?: string;
  supportSEO?: boolean;
}

export const ListingForLandingBlog = (props: ListingForLandingBlogProps) => {
  const {
    data,
    renderCard,
    renderSeparator = () => null,
    renderKey,
    pageSize = 10,
    pageInfo,
    onSearch,
    searchPlaceholder = 'Nhập chủ đề hoặc từ khóa cần tìm',
    searchResults,
    supportSEO = false,
    currentPageUrl,
  } = props;
  const searchText = React.useRef('');
  const router = useRouter();

  const onSearchHandler = () => {
    onSearch(searchText.current);
  };

  return (
    <Main className="listing-with-search">
      {onSearch && (
        <div className="search-header">
          <input
            onChange={(e) => {
              searchText.current = e.target.value;
            }}
            ref={(element) => ((element || ({} as any)).onsearch = onSearchHandler)}
            placeholder={searchPlaceholder}
            type="search"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearchHandler();
              }
            }}
          />
          <div className="icon-wrapper">
            <IconLandingBlogSearch />
          </div>
        </div>
      )}
      {searchResults ? (
        <ResponsiveTypography.Paragraph color="black100" variant="semi-bold/16" mb="16px">
          {searchResults.count}{' '}
          <ResponsiveTypography.Text variant="regular/16" color="gray100">
            Kết quả tìm kiếm cho từ khoá
          </ResponsiveTypography.Text>{' '}
          “{searchResults.keyword}”.
        </ResponsiveTypography.Paragraph>
      ) : null}
      {!data || data?.length === 0 ? (
        <div className="empty-state">
          <ImageEmptyResult />
          <ResponsiveTypography.Paragraph variant="semi-bold/32" color="gray60">
            Không tìm thấy kết quả
          </ResponsiveTypography.Paragraph>
        </div>
      ) : (
        <div className="list">
          {data.map((item, index) => {
            return (
              <React.Fragment key={renderKey || index}>
                {renderCard({ item, index })}
                {renderSeparator()}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {pageInfo?.totalPages > 0 && (
        <PaginationComponent
          hideOnSinglePage
          totalPage={pageInfo.totalPages}
          numberPerPage={pageSize}
          key={pageInfo.totalPages}
          onChange={(page) => {
            router.push({
              pathname: `${getLandingUrlFromPath(currentPageUrl, 'landing-blog')}/page/${page}`,
            });
          }}
          defaultPageSize={pageSize}
          current={!isNaN(pageInfo?.number) ? pageInfo?.number + 1 : pageInfo?.current}
          total={pageInfo?.totalElements || pageInfo?.totalResults}
          showSizeChanger={false}
          itemRender={(pageNum, _, originalElement) => {
            if (supportSEO) {
              return (
                <Link
                  href={{
                    pathname: `${getLandingUrlFromPath(
                      currentPageUrl,
                      'landing-blog'
                    )}/page/${pageNum}`,
                  }}
                >
                  {originalElement}
                </Link>
              );
            }
            return originalElement;
          }}
        />
      )}
    </Main>
  );
};
